<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>

    <div class="page-wrapper">
      <div class="content campaign-page">
        <div class="page-header">
          <div class="page-title">
            <h4>WhatsApp Campaigns</h4>
            <h6>Create offer campaigns and track delivery by customer</h6>
          </div>
          <div class="page-btn">
            <button class="btn btn-added" type="button" @click="toggleCreatePanel">
              <vue-feather type="plus-circle"></vue-feather>
              Add Campaign
            </button>
          </div>
        </div>

        <div v-if="error" class="alert alert-danger campaign-alert">{{ error }}</div>

        <div class="campaign-grid">
          <div class="campaign-list card">
            <div class="card-body">
              <div class="campaign-list-head">
                <div>
                  <h5>Campaigns</h5>
                  <span>{{ campaigns.length }} campaign{{ campaigns.length === 1 ? "" : "s" }}</span>
                </div>
                <button class="btn btn-outline-primary btn-sm" type="button" @click="refreshAll">
                  <vue-feather type="refresh-cw"></vue-feather>
                  Refresh
                </button>
              </div>

              <div v-if="loading" class="empty-state">Loading campaigns...</div>
              <div v-else-if="!campaigns.length" class="empty-state">
                <vue-feather type="send"></vue-feather>
                <strong>No campaigns yet</strong>
                <span>Use Add Campaign to create your first offer.</span>
              </div>

              <div
                v-for="campaign in campaigns"
                v-else
                :key="campaign.campaignid"
                class="campaign-card"
                :class="{ active: selected && selected.campaignid === campaign.campaignid }"
                role="button"
                tabindex="0"
                @click="selectCampaign(campaign.campaignid)"
                @keydown.enter="selectCampaign(campaign.campaignid)"
              >
                <div class="campaign-card-top">
                  <div>
                    <strong>{{ campaign.name }}</strong>
                    <span>{{ campaign.meta_template_name }}</span>
                  </div>
                  <span class="status-badge" :class="statusClass(campaign.status)">
                    {{ titleCase(campaign.status) }}
                  </span>
                </div>

                <div class="campaign-progress-row">
                  <div class="progress">
                    <div
                      class="progress-bar"
                      :class="progressClass(campaign.status)"
                      :style="{ width: progressPercent(campaign) + '%' }"
                    ></div>
                  </div>
                  <span>{{ campaign.sent_count }} / {{ campaign.total_recipients }}</span>
                </div>

                <div class="campaign-meta">
                  <span>{{ campaign.sent_count }} sent</span>
                  <span>{{ campaign.failed_count }} failed</span>
                  <span>{{ timelineText(campaign) }}</span>
                </div>

                <div v-if="campaign.status === 'DRAFT'" class="campaign-card-actions">
                  <button class="btn btn-submit btn-sm" type="button" @click.stop="selectCampaign(campaign.campaignid)">
                    <vue-feather type="users"></vue-feather>
                    Review
                  </button>
                </div>
              </div>
            </div>
          </div>

        <div v-if="showCreatePanel" class="campaign-modal-backdrop" @click.self="toggleCreatePanel">
          <div class="campaign-modal">
            <div class="campaign-modal-head">
              <div>
                <h5>Add Campaign</h5>
                <span>Save as Draft first, then review and start when ready.</span>
              </div>
              <button class="modal-close" type="button" @click="toggleCreatePanel" aria-label="Close">
                <vue-feather type="x"></vue-feather>
              </button>
            </div>

            <form @submit.prevent="createCampaign">
              <div class="modal-form-grid">
                <div>
                  <div class="form-group">
                    <label>Campaign name</label>
                    <input
                      v-model.trim="form.name"
                      class="form-control"
                      placeholder="Diwali offer 2026"
                      required
                    />
                  </div>

                  <div class="form-group">
                    <label>Audience</label>
                    <select v-model="form.audienceMode" class="form-select">
                      <option value="all_opted_in">All opted-in customers</option>
                      <option value="location">By location</option>
                      <option value="manual">Manual customer select</option>
                    </select>
                  </div>

                  <div v-if="form.audienceMode === 'location'" class="form-group">
                    <label>Location</label>
                    <select v-model="form.locationid" class="form-select" required>
                      <option value="">Select location</option>
                      <option
                        v-for="location in locations"
                        :key="location.locationid"
                        :value="location.locationid"
                      >
                        {{ location.locationname }}
                      </option>
                    </select>
                  </div>

                  <div v-if="form.audienceMode === 'manual'" class="form-group">
                    <label>Customers</label>
                    <select v-model="form.customerids" class="form-select manual-select" multiple required>
                      <option
                        v-for="customer in customers"
                        :key="customer.customerid"
                        :value="customer.customerid"
                      >
                        {{ customer.customername }} - {{ customer.mobile || "No mobile" }}
                      </option>
                    </select>
                  </div>

                  <div class="form-group">
                    <label>Offer image</label>
                    <input class="form-control" type="file" accept="image/*" @change="handleImage" />
                  </div>
                </div>

                <div>
                  <div class="form-group">
                    <label>Message preview</label>
                    <textarea v-model="form.bodyText" class="form-control campaign-message" rows="6"></textarea>
                  </div>

                  <div class="template-preview">
                    <div v-if="imagePreview" class="offer-image">
                      <img :src="imagePreview" alt="Offer preview" />
                    </div>
                    <div v-else class="offer-placeholder">
                      <vue-feather type="image"></vue-feather>
                    </div>
                    <div class="template-body">
                      <span>Template: test_offer</span>
                      <p>{{ previewText }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="campaign-modal-actions">
                <button class="btn btn-cancel" type="button" @click="toggleCreatePanel">Cancel</button>
                <button class="btn btn-submit" type="submit" :disabled="saving">
                  <vue-feather v-if="!saving" type="save"></vue-feather>
                  {{ saving ? "Saving..." : "Save Draft" }}
                </button>
              </div>
            </form>
          </div>
        </div>

        <section v-if="selected" class="campaign-detail card">
          <div class="card-body">
            <div class="detail-head">
              <div>
                <div class="detail-title-row">
                  <h5>{{ selected.name }}</h5>
                  <span class="status-badge" :class="statusClass(selected.status)">
                    {{ titleCase(selected.status) }}
                  </span>
                </div>
                <span class="detail-subtitle">{{ selected.meta_template_name }} | {{ timelineText(selected) }}</span>
              </div>
              <div class="detail-actions">
                <select v-model="recipientStatus" class="form-select" @change="changeRecipientStatus">
                  <option value="">All</option>
                  <option value="SENT">Sent</option>
                  <option value="FAILED">Failed</option>
                  <option value="PENDING">Pending</option>
                  <option value="PROCESSING">Processing</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
                <button
                  v-if="selected.status === 'DRAFT'"
                  class="btn btn-submit"
                  type="button"
                  :disabled="!selectedAudienceCount"
                  @click="startReviewedCampaign"
                >
                  <vue-feather type="play"></vue-feather>
                  Start
                </button>
                <button
                  class="btn btn-outline-primary"
                  type="button"
                  @click="retryFailed"
                  :disabled="selected.failed_count === 0"
                >
                  Retry Failed
                </button>
                <button
                  class="btn btn-outline-danger"
                  type="button"
                  @click="cancelCampaign"
                  :disabled="selected.status !== 'SENDING'"
                >
                  Cancel
                </button>
              </div>
            </div>

            <div class="summary-grid">
              <div>
                <span>Total recipients</span>
                <strong>{{ selected.total_recipients }}</strong>
              </div>
              <div>
                <span>Sent</span>
                <strong class="text-success">{{ selected.sent_count }}</strong>
              </div>
              <div>
                <span>Failed</span>
                <strong class="text-danger">{{ selected.failed_count }}</strong>
              </div>
              <div>
                <span>Pending</span>
                <strong>{{ remainingCount }}</strong>
              </div>
            </div>

            <div v-if="selected.status === 'DRAFT'" class="audience-review">
              <div class="audience-review-head">
                <div>
                  <strong>Review recipients</strong>
                  <span>Sending to {{ selectedAudienceCount }} of {{ audiencePreview.total }} customers</span>
                </div>
                <div class="audience-actions">
                  <button class="btn btn-outline-primary btn-sm" type="button" @click="selectAllAudience">
                    Select all
                  </button>
                  <button class="btn btn-outline-danger btn-sm" type="button" @click="deselectAllAudience">
                    Deselect all
                  </button>
                </div>
              </div>

              <div class="audience-toolbar">
                <div class="search-set">
                  <vue-feather type="search"></vue-feather>
                  <input
                    v-model.trim="audienceSearch"
                    class="form-control"
                    placeholder="Search name or phone"
                    @input="queueAudienceSearch"
                  />
                </div>
                <select v-model.number="audiencePageSize" class="form-select" @change="changeAudiencePageSize">
                  <option :value="25">25 / page</option>
                  <option :value="50">50 / page</option>
                  <option :value="100">100 / page</option>
                  <option :value="200">200 / page</option>
                </select>
              </div>

              <div class="table-responsive audience-table-wrap">
                <table class="table audience-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          class="form-check-input"
                          type="checkbox"
                          :checked="visibleAudienceSelected"
                          :disabled="!audiencePreview.items.length"
                          @change="toggleVisibleAudience($event.target.checked)"
                        />
                      </th>
                      <th>Customer</th>
                      <th>Mobile</th>
                      <th>Outstanding</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="audienceLoading">
                      <td colspan="4" class="text-center text-muted">Loading audience...</td>
                    </tr>
                    <tr v-for="customer in audiencePreview.items" v-else :key="customer.customerid">
                      <td>
                        <input
                          class="form-check-input"
                          type="checkbox"
                          :checked="isAudienceCustomerSelected(customer.customerid)"
                          @change="toggleAudienceCustomer(customer.customerid, $event.target.checked)"
                        />
                      </td>
                      <td>
                        <strong>{{ customer.customername }}</strong>
                      </td>
                      <td>{{ customer.mobile || "-" }}</td>
                      <td>{{ formatCurrency(customer.outstanding_bal) }}</td>
                    </tr>
                    <tr v-if="!audienceLoading && !audiencePreview.items.length">
                      <td colspan="4" class="text-center text-muted">No customers match this audience</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="recipient-pagination">
                <div>
                  Showing {{ audienceStart }}-{{ audienceEnd }} of {{ audiencePreview.filtered_total }} matched customers
                </div>
                <div class="recipient-pagination-controls">
                  <button
                    class="btn btn-outline-primary btn-sm"
                    type="button"
                    :disabled="audiencePage <= 1"
                    @click="changeAudiencePage(audiencePage - 1)"
                  >
                    Previous
                  </button>
                  <span>Page {{ audiencePage }} / {{ audienceTotalPages }}</span>
                  <button
                    class="btn btn-outline-primary btn-sm"
                    type="button"
                    :disabled="audiencePage >= audienceTotalPages"
                    @click="changeAudiencePage(audiencePage + 1)"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>

            <div class="delivery-log">
              <div class="delivery-log-head">
                <div>
                  <strong>Delivery log</strong>
                  <span>{{ workerHint }}</span>
                </div>
                <button class="btn btn-outline-primary btn-sm" type="button" @click="loadDetail(selected.campaignid)">
                  Refresh
                </button>
              </div>
              <div class="delivery-log-grid">
                <div v-for="item in statusLogItems" :key="item.label">
                  <span>{{ item.label }}</span>
                  <strong>{{ item.value }}</strong>
                </div>
              </div>
              <div v-if="selected.recent_errors && selected.recent_errors.length" class="recent-errors">
                <strong>Recent errors</strong>
                <ul>
                  <li v-for="entry in selected.recent_errors" :key="entry.id">
                    <span>{{ entry.customername || entry.mobile }}</span>
                    <p :title="entry.error_message">{{ formatDeliveryDetail(entry.error_message) }}</p>
                  </li>
                </ul>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table campaign-recipient-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Mobile</th>
                    <th>Status</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="recipient in selected.recipients" :key="recipient.id">
                    <td>
                      <strong>{{ recipient.customername || recipient.variables?.["1"] || recipient.customerid }}</strong>
                    </td>
                    <td>{{ maskMobile(recipient.mobile) }}</td>
                    <td>
                      <span class="status-badge" :class="statusClass(recipient.status)">
                        {{ titleCase(recipient.status) }}
                      </span>
                    </td>
                    <td>
                      <div
                        class="recipient-detail"
                        :class="{ 'error-text': recipient.status === 'FAILED' }"
                        :title="recipient.error_message || recipient.provider_message_id || '-'"
                      >
                        {{ formatDeliveryDetail(recipient.error_message || recipient.provider_message_id) }}
                      </div>
                    </td>
                  </tr>
                  <tr v-if="!selected.recipients.length">
                    <td colspan="4" class="text-center text-muted">No recipients for this filter</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="selected.recipient_total" class="recipient-pagination">
              <div>
                Showing {{ recipientStart }}-{{ recipientEnd }} of {{ selected.recipient_total }} recipients
              </div>
              <div class="recipient-pagination-controls">
                <select v-model.number="recipientPageSize" class="form-select" @change="changeRecipientPageSize">
                  <option :value="25">25 / page</option>
                  <option :value="50">50 / page</option>
                  <option :value="100">100 / page</option>
                  <option :value="200">200 / page</option>
                </select>
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  :disabled="recipientPage <= 1"
                  @click="changeRecipientPage(recipientPage - 1)"
                >
                  Previous
                </button>
                <span>Page {{ recipientPage }} / {{ recipientTotalPages }}</span>
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  :disabled="recipientPage >= recipientTotalPages"
                  @click="changeRecipientPage(recipientPage + 1)"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const defaultBodyText = "Hi {{1}},\nFestival Sale is Live!\nVisit our showroom today.";

export default {
  data() {
    return {
      campaigns: [],
      selected: null,
      customers: [],
      locations: [],
      recipientStatus: "",
      loading: false,
      saving: false,
      error: "",
      imageFile: null,
      imagePreview: "",
      poller: null,
      recipientPage: 1,
      recipientPageSize: 50,
      audiencePreview: {
        items: [],
        total: 0,
        filtered_total: 0,
        page: 1,
        page_size: 50,
      },
      audienceLoading: false,
      audienceSearch: "",
      audienceSearchTimer: null,
      audiencePage: 1,
      audiencePageSize: 50,
      audienceSelectionMode: "all",
      audienceIncludedIds: [],
      audienceExcludedIds: [],
      showCreatePanel: false,
      form: {
        name: "",
        audienceMode: "all_opted_in",
        locationid: "",
        customerids: [],
        bodyText: defaultBodyText,
      },
    };
  },
  computed: {
    sampleCustomerName() {
      const customer = this.customers.find((item) => this.form.customerids.includes(item.customerid));
      return customer?.customername || "Customer";
    },
    previewText() {
      return this.form.bodyText.replace("{{1}}", this.sampleCustomerName);
    },
    remainingCount() {
      if (!this.selected) return 0;
      return Math.max(
        Number(this.selected.total_recipients || 0) -
          Number(this.selected.sent_count || 0) -
          Number(this.selected.failed_count || 0),
        0
      );
    },
    recipientTotalPages() {
      const total = Number(this.selected?.recipient_total || 0);
      return Math.max(Math.ceil(total / Number(this.recipientPageSize || 50)), 1);
    },
    recipientStart() {
      const total = Number(this.selected?.recipient_total || 0);
      if (!total) return 0;
      return (Number(this.recipientPage || 1) - 1) * Number(this.recipientPageSize || 50) + 1;
    },
    recipientEnd() {
      const total = Number(this.selected?.recipient_total || 0);
      return Math.min(Number(this.recipientPage || 1) * Number(this.recipientPageSize || 50), total);
    },
    selectedAudienceCount() {
      const total = Number(this.audiencePreview.total || 0);
      if (this.audienceSelectionMode === "all") {
        return Math.max(total - this.audienceExcludedIds.length, 0);
      }
      return this.audienceIncludedIds.length;
    },
    audienceTotalPages() {
      const total = Number(this.audiencePreview.filtered_total || 0);
      return Math.max(Math.ceil(total / Number(this.audiencePageSize || 50)), 1);
    },
    audienceStart() {
      const total = Number(this.audiencePreview.filtered_total || 0);
      if (!total) return 0;
      return (Number(this.audiencePage || 1) - 1) * Number(this.audiencePageSize || 50) + 1;
    },
    audienceEnd() {
      const total = Number(this.audiencePreview.filtered_total || 0);
      return Math.min(Number(this.audiencePage || 1) * Number(this.audiencePageSize || 50), total);
    },
    visibleAudienceSelected() {
      const items = this.audiencePreview.items || [];
      return items.length > 0 && items.every((customer) => this.isAudienceCustomerSelected(customer.customerid));
    },
    statusLogItems() {
      const counts = this.selected?.status_counts || {};
      return [
        { label: "Pending", value: Number(counts.PENDING || 0) },
        { label: "Processing", value: Number(counts.PROCESSING || 0) },
        { label: "Sent", value: Number(counts.SENT || 0) },
        { label: "Failed", value: Number(counts.FAILED || 0) },
        { label: "Cancelled", value: Number(counts.CANCELLED || 0) },
      ];
    },
    workerHint() {
      if (!this.selected) return "";
      const counts = this.selected.status_counts || {};
      const pending = Number(counts.PENDING || 0);
      const processing = Number(counts.PROCESSING || 0);
      const failed = Number(counts.FAILED || 0);
      if (this.selected.status === "SENDING" && pending > 0 && processing === 0) {
        return "Waiting for background worker to pick pending recipients.";
      }
      if (this.selected.status === "SENDING" && processing > 0) {
        return "Background worker has recipients in progress.";
      }
      if (failed > 0) {
        return "Provider or delivery errors were recorded below.";
      }
      return "No delivery errors recorded.";
    },
  },
  mounted() {
    this.refreshAll();
    this.loadLookups();
    this.poller = setInterval(() => {
      if (this.campaigns.some((campaign) => campaign.status === "SENDING")) {
        this.refreshAll(false);
      }
    }, 4000);
  },
  beforeUnmount() {
    if (this.poller) clearInterval(this.poller);
    if (this.audienceSearchTimer) clearTimeout(this.audienceSearchTimer);
  },
  methods: {
    async refreshAll(showLoading = true) {
      if (showLoading) this.loading = true;
      try {
        const { data } = await axios.get("/v1/campaigns");
        this.campaigns = data || [];
        if (!this.selected && this.campaigns.length) {
          await this.loadDetail(this.campaigns[0].campaignid, false);
        } else if (this.selected) {
          await this.loadDetail(this.selected.campaignid, false);
        }
      } catch (err) {
        this.error = err.response?.data?.detail || "Unable to load campaigns";
      } finally {
        this.loading = false;
      }
    },
    async loadLookups() {
      try {
        const [customersRes, locationsRes] = await Promise.all([
          axios.get("/v1/customers"),
          axios.get("/v1/locations"),
        ]);
        this.customers = Array.isArray(customersRes.data) ? customersRes.data : customersRes.data?.items || [];
        this.locations = Array.isArray(locationsRes.data) ? locationsRes.data : locationsRes.data?.items || [];
      } catch (err) {
        this.error = err.response?.data?.detail || "Unable to load campaign audience data";
      }
    },
    toggleCreatePanel() {
      this.showCreatePanel = !this.showCreatePanel;
    },
    handleImage(event) {
      const file = event.target.files?.[0];
      this.imageFile = file || null;
      this.imagePreview = file ? URL.createObjectURL(file) : "";
    },
    audienceFilter() {
      if (this.form.audienceMode === "location") {
        return { mode: "location", locationid: this.form.locationid };
      }
      if (this.form.audienceMode === "manual") {
        return { mode: "manual", customerids: this.form.customerids };
      }
      return { mode: "all_opted_in" };
    },
    async createCampaign() {
      this.error = "";
      this.saving = true;
      try {
        const body = new FormData();
        body.append("name", this.form.name);
        body.append("audience_filter", JSON.stringify(this.audienceFilter()));
        body.append("meta_template_name", "test_offer");
        body.append("language_code", "en");
        body.append("body_text_preview", this.form.bodyText || defaultBodyText);
        if (this.imageFile) body.append("offer_image", this.imageFile);

        const created = await axios.post("/v1/campaigns", body, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.resetForm();
        this.showCreatePanel = false;
        await this.refreshAll();
        await this.selectCampaign(created.data.campaignid);
      } catch (err) {
        this.error = err.response?.data?.detail || "Unable to save campaign";
      } finally {
        this.saving = false;
      }
    },
    resetForm() {
      this.form = {
        name: "",
        audienceMode: "all_opted_in",
        locationid: "",
        customerids: [],
        bodyText: defaultBodyText,
      };
      this.imageFile = null;
      this.imagePreview = "";
    },
    async selectCampaign(campaignid) {
      this.recipientStatus = "";
      this.recipientPage = 1;
      this.resetAudienceReview();
      await this.loadDetail(campaignid);
    },
    async loadDetail(campaignid, showErrors = true) {
      try {
        const params = {
          page: this.recipientPage,
          page_size: this.recipientPageSize,
        };
        if (this.recipientStatus) params.status = this.recipientStatus;
        const { data } = await axios.get(`/v1/campaigns/${campaignid}`, { params });
        this.selected = data;
        if (this.recipientPage > this.recipientTotalPages) {
          this.recipientPage = this.recipientTotalPages;
          await this.loadDetail(campaignid, showErrors);
        }
        if (this.selected?.status === "DRAFT") {
          await this.loadAudiencePreview();
        } else {
          this.resetAudienceReview(false);
        }
      } catch (err) {
        if (showErrors) this.error = err.response?.data?.detail || "Unable to load campaign details";
      }
    },
    resetAudienceReview(resetSelection = true) {
      this.audiencePreview = {
        items: [],
        total: 0,
        filtered_total: 0,
        page: 1,
        page_size: this.audiencePageSize,
      };
      this.audienceSearch = "";
      this.audiencePage = 1;
      if (resetSelection) {
        this.audienceSelectionMode = "all";
        this.audienceIncludedIds = [];
        this.audienceExcludedIds = [];
      }
    },
    async loadAudiencePreview() {
      if (!this.selected || this.selected.status !== "DRAFT") return;
      this.audienceLoading = true;
      try {
        const params = {
          page: this.audiencePage,
          page_size: this.audiencePageSize,
        };
        if (this.audienceSearch) params.search = this.audienceSearch;
        const { data } = await axios.get(`/v1/campaigns/${this.selected.campaignid}/audience-preview`, { params });
        this.audiencePreview = {
          items: data.items || [],
          total: Number(data.total || 0),
          filtered_total: Number(data.filtered_total || data.total || 0),
          page: Number(data.page || this.audiencePage),
          page_size: Number(data.page_size || this.audiencePageSize),
        };
        if (this.audiencePage > this.audienceTotalPages) {
          this.audiencePage = this.audienceTotalPages;
          await this.loadAudiencePreview();
        }
      } catch (err) {
        this.error = err.response?.data?.detail || "Unable to load audience preview";
      } finally {
        this.audienceLoading = false;
      }
    },
    queueAudienceSearch() {
      if (this.audienceSearchTimer) clearTimeout(this.audienceSearchTimer);
      this.audienceSearchTimer = setTimeout(async () => {
        this.audiencePage = 1;
        await this.loadAudiencePreview();
      }, 300);
    },
    async changeAudiencePage(page) {
      this.audiencePage = Math.min(Math.max(Number(page) || 1, 1), this.audienceTotalPages);
      await this.loadAudiencePreview();
    },
    async changeAudiencePageSize() {
      this.audiencePage = 1;
      await this.loadAudiencePreview();
    },
    selectAllAudience() {
      this.audienceSelectionMode = "all";
      this.audienceIncludedIds = [];
      this.audienceExcludedIds = [];
    },
    deselectAllAudience() {
      this.audienceSelectionMode = "manual";
      this.audienceIncludedIds = [];
      this.audienceExcludedIds = [];
    },
    isAudienceCustomerSelected(customerid) {
      if (this.audienceSelectionMode === "all") {
        return !this.audienceExcludedIds.includes(customerid);
      }
      return this.audienceIncludedIds.includes(customerid);
    },
    toggleAudienceCustomer(customerid, checked) {
      if (this.audienceSelectionMode === "all") {
        this.audienceExcludedIds = checked
          ? this.audienceExcludedIds.filter((id) => id !== customerid)
          : Array.from(new Set([...this.audienceExcludedIds, customerid]));
        return;
      }
      this.audienceIncludedIds = checked
        ? Array.from(new Set([...this.audienceIncludedIds, customerid]))
        : this.audienceIncludedIds.filter((id) => id !== customerid);
    },
    toggleVisibleAudience(checked) {
      const ids = (this.audiencePreview.items || []).map((customer) => customer.customerid);
      ids.forEach((customerid) => this.toggleAudienceCustomer(customerid, checked));
    },
    async changeRecipientStatus() {
      if (!this.selected) return;
      this.recipientPage = 1;
      await this.loadDetail(this.selected.campaignid);
    },
    async changeRecipientPage(page) {
      if (!this.selected) return;
      this.recipientPage = Math.min(Math.max(Number(page) || 1, 1), this.recipientTotalPages);
      await this.loadDetail(this.selected.campaignid);
    },
    async changeRecipientPageSize() {
      if (!this.selected) return;
      this.recipientPage = 1;
      await this.loadDetail(this.selected.campaignid);
    },
    async retryFailed() {
      if (!this.selected) return;
      await axios.post(`/v1/campaigns/${this.selected.campaignid}/retry-failed`);
      await this.refreshAll();
    },
    async startReviewedCampaign() {
      if (!this.selected) return;
      const payload = this.audienceSelectionMode === "all"
        ? { excluded_customerids: this.audienceExcludedIds }
        : { included_customerids: this.audienceIncludedIds, excluded_customerids: [] };
      await this.startCampaign(this.selected.campaignid, payload);
    },
    async startCampaign(campaignid, payload = {}) {
      this.error = "";
      try {
        await axios.post(`/v1/campaigns/${campaignid}/start`, payload);
        await this.refreshAll();
        await this.selectCampaign(campaignid);
      } catch (err) {
        this.error = err.response?.data?.detail || "Unable to start campaign";
      }
    },
    async cancelCampaign() {
      if (!this.selected) return;
      await axios.post(`/v1/campaigns/${this.selected.campaignid}/cancel`);
      await this.refreshAll();
    },
    progressPercent(campaign) {
      const total = Number(campaign.total_recipients || 0);
      if (!total) return 0;
      return Math.min(Math.round((Number(campaign.sent_count || 0) / total) * 100), 100);
    },
    progressClass(status) {
      return String(status || "").toLowerCase() === "sending" ? "sending" : "completed";
    },
    statusClass(status) {
      return `status-${String(status || "").toLowerCase()}`;
    },
    titleCase(value) {
      return String(value || "").toLowerCase().replace(/(^|_|\s)\w/g, (match) => match.toUpperCase());
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString() : "-";
    },
    formatCurrency(value) {
      const amount = Number(value || 0);
      return amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    timelineText(campaign) {
      if (campaign.completed_at) return `Completed ${this.formatDate(campaign.completed_at)}`;
      if (campaign.started_at) return `Started ${this.formatDate(campaign.started_at)}`;
      return "Not started";
    },
    formatDeliveryDetail(value) {
      const raw = String(value || "").trim();
      if (!raw) return "-";

      const jsonStart = raw.indexOf("{");
      if (jsonStart >= 0) {
        try {
          const parsed = JSON.parse(raw.slice(jsonStart));
          const error = parsed.error || {};
          const message = error.error_data?.details || error.message;
          if (message) return this.friendlyDeliveryMessage(message);
        } catch (err) {
          // Fall back to the readable cleanup below.
        }
      }

      return this.friendlyDeliveryMessage(raw
        .replace(/^WhatsApp provider error:\s*/i, "")
        .replace(/\s+/g, " "));
    },
    friendlyDeliveryMessage(message) {
      const text = String(message || "").trim();
      if (/not in allowed list/i.test(text)) {
        return "Recipient is not in the allowed WhatsApp test list. Add the number in Meta, or switch WhatsApp app to live mode.";
      }
      return text || "-";
    },
    maskMobile(mobile) {
      const value = String(mobile || "");
      if (value.length <= 4) return value || "-";
      return `${value.slice(0, 3)}xxxxxx${value.slice(-2)}`;
    },
  },
};
</script>

<style scoped>
.campaign-page {
  padding-bottom: 32px;
}

.campaign-alert {
  margin-bottom: 16px;
}

.campaign-grid {
  display: grid;
  grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.campaign-list {
  position: sticky;
  top: 78px;
  max-height: calc(100vh - 104px);
  overflow: hidden;
}

.campaign-list .card-body {
  max-height: calc(100vh - 104px);
  overflow-y: auto;
}

.campaign-list-head,
.campaign-card-top,
.campaign-progress-row,
.campaign-meta,
.campaign-card-actions,
.campaign-modal-head,
.campaign-modal-actions,
.detail-head,
.detail-actions,
.detail-title-row {
  display: flex;
  align-items: center;
}

.campaign-list-head {
  position: sticky;
  top: 0;
  z-index: 2;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  background: #fff;
}

.campaign-list-head h5,
.section-title h5,
.detail-head h5 {
  margin: 0 0 4px;
  color: #1f2937;
  font-weight: 700;
}

.campaign-list-head span,
.section-title span,
.detail-subtitle,
.campaign-card-top span,
.campaign-meta,
.summary-grid span {
  color: #6b7280;
  font-size: 13px;
}

.campaign-card {
  width: 100%;
  margin-bottom: 12px;
  padding: 14px 15px;
  text-align: left;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.campaign-card:hover,
.campaign-card.active {
  border-color: #ff9f43;
  box-shadow: 0 6px 18px rgba(33, 33, 33, 0.08);
}

.campaign-card-top {
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.campaign-card-top strong {
  display: block;
  margin-bottom: 3px;
  color: #111827;
  font-size: 15px;
}

.campaign-progress-row {
  gap: 12px;
  margin-bottom: 9px;
}

.campaign-progress-row .progress {
  flex: 1;
  height: 7px;
  background: #edf1f3;
  border-radius: 999px;
}

.campaign-progress-row > span {
  min-width: 78px;
  color: #4b5563;
  font-size: 13px;
  font-weight: 700;
  text-align: right;
}

.progress-bar {
  border-radius: 999px;
}

.progress-bar.sending {
  background: #2563eb;
}

.progress-bar.completed {
  background: #28c76f;
}

.campaign-meta {
  gap: 10px;
  flex-wrap: wrap;
}

.campaign-card-actions {
  justify-content: flex-end;
  margin-top: 14px;
}

.campaign-card-actions .btn svg,
.detail-actions .btn svg,
.campaign-modal-actions .btn svg {
  width: 14px;
  height: 14px;
  margin-right: 6px;
}

.campaign-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(17, 24, 39, 0.45);
}

.campaign-modal {
  width: min(880px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.22);
}

.campaign-modal-head {
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px;
  border-bottom: 1px solid #edf0f2;
}

.campaign-modal-head h5 {
  margin: 0 0 4px;
  color: #1f2937;
  font-weight: 700;
}

.campaign-modal-head span {
  color: #6b7280;
  font-size: 13px;
}

.modal-close {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #4b5563;
}

.modal-close svg {
  width: 18px;
  height: 18px;
}

.modal-form-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 18px;
  padding: 20px 22px 8px;
}

.campaign-modal-actions {
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 22px 20px;
  border-top: 1px solid #edf0f2;
}

.form-group {
  margin-bottom: 14px;
}

.form-group label {
  display: block;
  margin-bottom: 7px;
  color: #374151;
  font-size: 13px;
  font-weight: 700;
}

.manual-select {
  min-height: 120px;
}

.campaign-message {
  resize: vertical;
}

.template-preview {
  overflow: hidden;
  margin: 16px 0;
  border: 1px solid #dfe5e8;
  border-radius: 8px;
}

.offer-image,
.offer-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 150px;
  background: #f5f7f9;
}

.offer-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.offer-placeholder svg {
  width: 40px;
  height: 40px;
  color: #9ca3af;
}

.template-body {
  padding: 14px;
}

.template-body span {
  display: block;
  margin-bottom: 8px;
  color: #ff9f43;
  font-size: 12px;
  font-weight: 800;
}

.template-body p {
  margin: 0;
  color: #1f2937;
  white-space: pre-line;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 26px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.status-draft,
.status-pending {
  color: #475569;
  background: #eef2f7;
}

.status-sending,
.status-processing {
  color: #075985;
  background: #e0f2fe;
}

.status-completed,
.status-sent {
  color: #166534;
  background: #dcfce7;
}

.status-failed {
  color: #991b1b;
  background: #fee2e2;
}

.status-cancelled {
  color: #6b21a8;
  background: #f3e8ff;
}

.campaign-detail {
  min-width: 0;
  margin-top: 0;
}

.detail-head {
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.detail-title-row {
  gap: 10px;
  flex-wrap: wrap;
}

.detail-actions {
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}

.detail-actions .form-select {
  min-width: 135px;
  width: auto;
}

.detail-actions .btn {
  min-height: 38px;
  white-space: nowrap;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.summary-grid div {
  min-width: 0;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 8px;
}

.summary-grid strong {
  display: block;
  margin-top: 5px;
  color: #111827;
  font-size: 22px;
  line-height: 1;
}

.delivery-log {
  margin-bottom: 16px;
  padding: 14px;
  background: #fbfcfe;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.audience-review {
  margin-bottom: 16px;
  padding: 14px;
  background: #fffdf8;
  border: 1px solid #f4dfbd;
  border-radius: 8px;
}

.audience-review-head,
.audience-toolbar,
.audience-actions {
  display: flex;
  align-items: center;
}

.audience-review-head {
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 12px;
}

.audience-review-head strong {
  display: block;
  color: #111827;
  font-size: 14px;
}

.audience-review-head span {
  display: block;
  margin-top: 3px;
  color: #6b7280;
  font-size: 13px;
}

.audience-actions,
.audience-toolbar {
  gap: 8px;
  flex-wrap: wrap;
}

.audience-toolbar {
  justify-content: space-between;
  margin-bottom: 12px;
}

.audience-toolbar .form-select {
  width: 118px;
  height: 38px;
  font-size: 13px;
}

.search-set {
  position: relative;
  flex: 1;
  min-width: 220px;
}

.search-set svg {
  position: absolute;
  top: 50%;
  left: 12px;
  width: 16px;
  height: 16px;
  color: #9ca3af;
  transform: translateY(-50%);
}

.search-set .form-control {
  padding-left: 38px;
}

.audience-table-wrap {
  max-height: 355px;
}

.audience-table {
  margin-bottom: 0;
  table-layout: fixed;
}

.audience-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  color: #4b5563;
  font-weight: 700;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.audience-table th:nth-child(1) {
  width: 46px;
}

.audience-table th:nth-child(2) {
  width: 42%;
}

.audience-table td {
  vertical-align: middle;
  border-bottom: 1px solid #edf0f2;
}

.audience-table strong {
  color: #111827;
  font-weight: 700;
}

.delivery-log-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.delivery-log-head strong,
.recent-errors strong {
  display: block;
  color: #111827;
  font-size: 14px;
}

.delivery-log-head span {
  display: block;
  margin-top: 3px;
  color: #6b7280;
  font-size: 12px;
}

.delivery-log-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(82px, 1fr));
  gap: 8px;
}

.delivery-log-grid div {
  padding: 9px 10px;
  background: #fff;
  border: 1px solid #edf0f2;
  border-radius: 6px;
}

.delivery-log-grid span {
  display: block;
  color: #6b7280;
  font-size: 12px;
}

.delivery-log-grid strong {
  display: block;
  margin-top: 3px;
  color: #111827;
  font-size: 17px;
}

.recent-errors {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
}

.recent-errors ul {
  max-height: 180px;
  margin: 8px 0 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
}

.recent-errors li {
  display: grid;
  grid-template-columns: minmax(110px, 160px) minmax(0, 1fr);
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #eef2f7;
}

.recent-errors li:last-child {
  border-bottom: 0;
}

.recent-errors li span {
  color: #374151;
  font-size: 13px;
  font-weight: 700;
}

.recent-errors li p {
  margin: 0;
  color: #991b1b;
  font-size: 13px;
  line-height: 1.35;
}

.table-responsive {
  max-height: 430px;
  overflow: auto;
  border: 1px solid #edf0f2;
  border-radius: 8px;
}

.campaign-recipient-table {
  margin-bottom: 0;
  table-layout: fixed;
}

.campaign-recipient-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  color: #4b5563;
  font-weight: 700;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.campaign-recipient-table th:nth-child(1) {
  width: 28%;
}

.campaign-recipient-table th:nth-child(2) {
  width: 18%;
}

.campaign-recipient-table th:nth-child(3) {
  width: 15%;
}

.campaign-recipient-table td {
  vertical-align: middle;
  border-bottom: 1px solid #edf0f2;
}

.campaign-recipient-table td:last-child {
  white-space: normal;
}

.recipient-detail {
  display: -webkit-box;
  max-height: 42px;
  overflow: hidden;
  color: #4b5563;
  font-size: 13px;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.recipient-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  color: #4b5563;
  font-size: 13px;
}

.recipient-pagination-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.recipient-pagination-controls .form-select {
  width: 118px;
  height: 34px;
  padding-top: 4px;
  padding-bottom: 4px;
  font-size: 13px;
}

.recipient-pagination-controls span {
  min-width: 92px;
  color: #374151;
  font-weight: 700;
  text-align: center;
}

.error-text {
  color: #991b1b;
  font-weight: 500;
}

.empty-state {
  display: flex;
  min-height: 180px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: #6b7280;
  text-align: center;
}

.empty-state svg {
  color: #ff9f43;
}

@media (max-width: 1199px) {
  .campaign-grid {
    grid-template-columns: 1fr;
  }

  .campaign-list {
    position: static;
    max-height: none;
  }

  .campaign-list .card-body {
    max-height: 420px;
  }
}

@media (max-width: 767px) {
  .page-header,
  .detail-head,
  .detail-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .delivery-log-head,
  .recent-errors li {
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .delivery-log-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .table-responsive {
    max-height: 360px;
  }

  .recipient-pagination {
    align-items: stretch;
    flex-direction: column;
  }

  .recipient-pagination-controls {
    justify-content: flex-start;
  }

  .audience-review-head,
  .audience-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .campaign-card-top,
  .campaign-progress-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .campaign-progress-row > span {
    min-width: 0;
    text-align: left;
  }

  .campaign-modal-backdrop {
    align-items: flex-start;
    padding: 12px;
  }

  .modal-form-grid {
    grid-template-columns: 1fr;
  }

  .campaign-modal-actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }
}
</style>
